import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  HiX,
  HiOutlineCloudUpload,
  HiOutlineDocumentDownload,
  HiOutlineTrash,
  HiOutlineSparkles,
  HiOutlineEye,
} from "react-icons/hi";
import {
  listarDocumentos,
  subirDocumento,
  eliminarDocumento,
  generarResumenIA,
} from "../api";
import { toast } from "react-toastify";
import "../styles.css";

function ModalDocumento({ numero, onClose, usuario, onAbrirPanelIA }) {
  const [documentos, setDocumentos] = useState([]);
  const [file, setFile] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [resumiendoDoc, setResumiendoDoc] = useState(null);
  const [showConfirmEliminar, setShowConfirmEliminar] = useState(null);

  const esAdmin = usuario?.rol?.rol
    ? usuario.rol.rol === "admin"
    : usuario?.rol === "admin";

  const fetchDocs = async () => {
    try {
      const docs = await listarDocumentos(numero);
      setDocumentos(docs);
    } catch (err) {
      console.error("Error al cargar documentos:", err);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, [numero]);

  const handleAdd = async () => {
    if (!file) return;
    setSubiendo(true);
    try {
      const uploaded = await subirDocumento(numero, file);
      toast.success("Documento subido correctamente");
      setDocumentos((prev) => [
        ...prev,
        { nombre: uploaded.documento, ruta: "" },
      ]);
      setFile(null);
    } catch (err) {
      console.error("Error subiendo documento:", err);
      toast.error("No se pudo subir el documento.");
    } finally {
      setSubiendo(false);
    }
  };

  const handleResumirLocal = async (nombreDoc = null) => {
    const clave = nombreDoc || "__ALL__";
    setResumiendoDoc(clave);
    const toastId = toast.loading(
      nombreDoc
        ? `✨ Analizando y resumiendo "${nombreDoc}" con IA...`
        : "✨ Analizando todos los documentos subidos con IA..."
    );
    try {
      await generarResumenIA(numero, {
        origen: "local",
        documentoNombre: nombreDoc,
      });
      toast.update(toastId, {
        render: "Resumen IA generado a partir de los documentos subidos",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      await fetchDocs();
      if (typeof onAbrirPanelIA === "function") {
        onClose();
        onAbrirPanelIA("resumen");
      }
    } catch (err) {
      toast.update(toastId, {
        render: err.message || "Error al resumir el documento",
        type: "error",
        isLoading: false,
        autoClose: 4000,
      });
    } finally {
      setResumiendoDoc(null);
    }
  };

  const handleEliminar = async () => {
    try {
      await eliminarDocumento(numero, showConfirmEliminar);
      toast.success("Documento eliminado");
      setDocumentos((prev) =>
        prev.filter((d) => d.nombre !== showConfirmEliminar)
      );
    } catch (err) {
      console.error("Error eliminando documento:", err);
      toast.error("No se pudo eliminar el documento.");
    } finally {
      setShowConfirmEliminar(null);
    }
  };

  const docsLocales = documentos.filter(
    (d) => !(d.nombre || "").toLowerCase().startsWith("resumen_ia_")
  );

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal animate-fadeIn"
        style={{ maxWidth: "680px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <h2 style={{ margin: 0 }}>
            Documentos{" "}
            <span style={{ color: "var(--accent-primary)" }}>
              #{numero.toUpperCase()}
            </span>
          </h2>
          <button
            className="btn-icon"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
            }}
          >
            <HiX size={24} />
          </button>
        </div>

        <div className="documentos-section">
          <div
            className="lista-documentos-moderna"
            style={{ marginBottom: "28px" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <label
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "var(--text-secondary)",
                }}
              >
                Archivos Adjuntos ({documentos.length})
              </label>

              {docsLocales.length > 0 && (
                <button
                  type="button"
                  className="btn-doc-ia-all"
                  onClick={() => handleResumirLocal(null)}
                  disabled={Boolean(resumiendoDoc)}
                >
                  <HiOutlineSparkles size={16} />
                  <span>
                    {resumiendoDoc === "__ALL__"
                      ? "Resumiendo..."
                      : `Resumir todos los subidos (${docsLocales.length})`}
                  </span>
                </button>
              )}
            </div>

            {documentos.length > 0 ? (
              <div
                className="docs-grid"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                {documentos.map((d, idx) => {
                  const esResumenIA = (d.nombre || "")
                    .toLowerCase()
                    .startsWith("resumen_ia_");
                  return (
                    <div
                      key={idx}
                      className="doc-item"
                      style={{
                        background: esResumenIA
                          ? "rgba(37, 99, 235, 0.04)"
                          : "var(--glass-bg)",
                        border: esResumenIA
                          ? "1px solid rgba(37, 99, 235, 0.25)"
                          : "1px solid var(--glass-border)",
                        borderRadius: "var(--radius-md)",
                        padding: "12px 16px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      <a
                        href={`/uploads/${encodeURIComponent(d.nombre)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          color: "var(--text-primary)",
                          textDecoration: "none",
                          fontWeight: 500,
                          flex: 1,
                          minWidth: "200px",
                          wordBreak: "break-all",
                        }}
                      >
                        <HiOutlineDocumentDownload
                          size={20}
                          style={{
                            color: "var(--accent-primary)",
                            flexShrink: 0,
                          }}
                        />
                        <span>{d.nombre}</span>
                      </a>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {esResumenIA ? (
                          typeof onAbrirPanelIA === "function" && (
                            <button
                              type="button"
                              className="btn-doc-ia-item visor"
                              onClick={() => {
                                onClose();
                                onAbrirPanelIA("resumen");
                              }}
                              title="Abrir en el Visor IA integrado"
                            >
                              <HiOutlineEye size={15} />
                              <span>Ver en Visor IA</span>
                            </button>
                          )
                        ) : (
                          <button
                            type="button"
                            className="btn-doc-ia-item"
                            onClick={() => handleResumirLocal(d.nombre)}
                            disabled={Boolean(resumiendoDoc)}
                            title="Generar resumen IA a partir de este documento"
                          >
                            <HiOutlineSparkles size={15} />
                            <span>
                              {resumiendoDoc === d.nombre
                                ? "Resumiendo..."
                                : "Resumir IA"}
                            </span>
                          </button>
                        )}

                        {esAdmin && (
                          <button
                            className="btn-icon danger"
                            onClick={() => setShowConfirmEliminar(d.nombre)}
                            style={{
                              background: "transparent",
                              border: "none",
                              color: "var(--accent-danger)",
                              cursor: "pointer",
                              padding: "4px",
                              borderRadius: "4px",
                            }}
                            title="Eliminar documento"
                          >
                            <HiOutlineTrash size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "32px",
                  background: "rgba(255,255,255,0.02)",
                  borderRadius: "var(--radius-md)",
                  border: "1px dashed var(--glass-border)",
                }}
              >
                <p style={{ color: "var(--text-secondary)", margin: 0 }}>
                  No hay documentos disponibles. Sube un Pliego PDF abajo para
                  resumirlo o consultarlo con IA.
                </p>
              </div>
            )}
          </div>

          <div
            className="upload-section"
            style={{
              borderTop: "1px solid var(--glass-border)",
              paddingTop: "24px",
            }}
          >
            <label
              style={{
                display: "block",
                marginBottom: "12px",
                fontSize: "0.85rem",
                fontWeight: 700,
                textTransform: "uppercase",
                color: "var(--text-secondary)",
              }}
            >
              Subir nuevo documento (PCAP, PPT, Memoria...)
            </label>
            <div
              className="upload-container"
              style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
            >
              <div
                className="file-input-wrapper"
                style={{ flex: 1, minWidth: "220px", position: "relative" }}
              >
                <input
                  type="file"
                  onChange={(e) => setFile(e.target.files[0])}
                  style={{
                    width: "100%",
                    padding: "10px",
                    background: "var(--bg-main)",
                    border: "1px solid var(--glass-border)",
                    borderRadius: "var(--radius-md)",
                    color: "var(--text-primary)",
                  }}
                />
              </div>
              <button
                className="btn-nueva"
                onClick={handleAdd}
                disabled={!file || subiendo}
                style={{
                  padding: "0 24px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {subiendo ? (
                  "..."
                ) : (
                  <>
                    <HiOutlineCloudUpload size={20} /> Subir
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div
          className="modal-footer"
          style={{
            marginTop: "28px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button className="btn-gestion" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>

      {showConfirmEliminar && (
        <div className="modal-overlay" style={{ zIndex: 1100 }}>
          <div className="modal animate-fadeIn" style={{ maxWidth: "400px" }}>
            <h3 style={{ marginTop: 0 }}>Eliminar Documento</h3>
            <p
              style={{
                color: "var(--text-secondary)",
                marginBottom: "24px",
              }}
            >
              ¿Estás seguro de que quieres eliminar{" "}
              <strong style={{ color: "var(--text-primary)" }}>
                "{showConfirmEliminar}"
              </strong>
              ? Esta acción no se puede deshacer.
            </p>
            <div
              className="modal-footer"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
              }}
            >
              <button
                className="btn-gestion"
                onClick={() => setShowConfirmEliminar(null)}
              >
                Cancelar
              </button>
              <button
                className="btn-nueva danger"
                onClick={handleEliminar}
                style={{ background: "var(--accent-danger)" }}
              >
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

export default ModalDocumento;
